import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import type { SurfaceEntry } from '@/lib/sync/document-surfaces';
import {
  captureExcerpt, captureRefusalMessage, isCaptureRefusal, type CapturedExcerpt,
} from '@/lib/templates/api';
import { useUIStore } from '@/stores/use-ui-store';
import { isReadableTextSurface } from '../utils/template-sources';

export type PassageCapture =
  | { status: 'capturing' }
  | { status: 'captured'; excerpt: CapturedExcerpt; capturedAt: number }
  | { status: 'refused'; message: string };

export interface SelectionPassage {
  surfaceId: string | null;
  sourceTitle: string;
  markdown: string;
  templateId: string | null;
  capture: PassageCapture;
}

const BODY_FRAGMENT = 'prosemirror';

// One hour inside the server's 23-hour acceptance window for an excerpt.
export const PASSAGE_RECAPTURE_AFTER_MS = 22 * 60 * 60 * 1000;

function selectedIn(
  fragmentName: string,
  entries: readonly SurfaceEntry[],
  documentTitle: string,
): { surfaceId: string | null; sourceTitle: string } | null {
  if (fragmentName === BODY_FRAGMENT) return { surfaceId: null, sourceTitle: documentTitle };
  const entry = entries.find((e) => e.fragment === fragmentName && isReadableTextSurface(e));
  return entry ? { surfaceId: entry.id, sourceTitle: entry.title } : null;
}

export function useSelectionPassage({ entityId, entries, documentTitle }: {
  entityId: string;
  entries: readonly SurfaceEntry[];
  documentTitle: string;
}): {
  passage: SelectionPassage | null;
  clear: () => void;
  refreshIfStale: () => Promise<SelectionPassage | null>;
} {
  const [passage, setPassage] = useState<SelectionPassage | null>(null);
  const latestRef = useRef(0);

  const capture = useCallback((arrived: SelectionPassage): Promise<SelectionPassage | null> => {
    const sequence = ++latestRef.current;
    const settle = (next: SelectionPassage) => {
      if (sequence !== latestRef.current) return null;
      setPassage(next);
      return next;
    };
    return captureExcerpt({ entityId, surfaceId: arrived.surfaceId, markdown: arrived.markdown }).then(
      (excerpt) => settle({ ...arrived, capture: { status: 'captured', excerpt, capturedAt: Date.now() } }),
      (error: unknown) => {
        if (isCaptureRefusal(error)) {
          console.warn('selection_capture_refused', error);
        } else {
          Sentry.captureException(error);
          console.error('selection_capture_failed', error);
        }
        settle({
          ...arrived,
          capture: { status: 'refused', message: captureRefusalMessage(error, arrived.sourceTitle) },
        });
        return null;
      },
    );
  }, [entityId]);
  const pending = useUIStore((s) => s.selectionRequestByEntity[entityId] ?? null);

  useEffect(() => {
    if (pending === null) return;
    const request = useUIStore.getState().takeSelectionRequest(entityId);
    if (request === null) return;
    const where = selectedIn(request.fragmentName, entries, documentTitle);
    if (where === null) {
      console.warn('selection_request_unknown_fragment');
      return;
    }
    const arrived: SelectionPassage = {
      ...where,
      markdown: request.markdown,
      templateId: request.templateId,
      capture: { status: 'capturing' },
    };
    setPassage(arrived);
    void capture(arrived);
  }, [pending, entityId, entries, documentTitle, capture]);

  const clear = useCallback(() => {
    latestRef.current += 1;
    setPassage(null);
  }, []);

  const refreshIfStale = useCallback((): Promise<SelectionPassage | null> => {
    if (passage === null || passage.capture.status !== 'captured') return Promise.resolve(passage);
    if (Date.now() - passage.capture.capturedAt < PASSAGE_RECAPTURE_AFTER_MS) return Promise.resolve(passage);
    return capture(passage);
  }, [passage, capture]);

  return { passage, clear, refreshIfStale };
}
